import { captureException } from '@sentry/react';

const prefix = 'Checkout';
const labelStyle = `
    background-color: mediumslateblue;
    border-radius: 2px;
    color: white;
    line-height: 1.5;
    padding: 1px 4px;
    text-shadow: 0 1px 0px rgba(0, 0, 0, 0.2);
`;

const ignoredMessages: string[] = [];

type ConsoleMethod = 'debug' | 'error' | 'info' | 'log' | 'time' | 'timeEnd' | 'trace' | 'warn';

/**
 * Internal console wrapper with Sentry integration
 * @param {ConsoleMethod} type - Console method type
 * @param {...unknown} args - Arguments to log
 * @returns {void}
 */
function _console(type: ConsoleMethod, ...args: unknown[]): void {
  if (JSON.parse(localStorage.getItem('debugMode') ?? 'false') === true || import.meta.env.VITE_APP_ENVIRONMENT !== 'production') {
    if (type === 'time' || type === 'timeEnd') {
      (window.console[type] as (label?: string) => void)(`[${prefix}] ${String(args[0])}`);
    } else {
      const consoleArgs: unknown[] = [`%c${prefix}%c`, labelStyle, '', ...args];
      (window.console[type] as (...data: unknown[]) => void)(...consoleArgs);
    }
  }

  // always capture errors for sentry
  if (type === 'error' && !ignoredMessages.includes(String(args[0]))) {
    captureException(reduceConsoleArgs(args), { level: 'error' });
  }
}

/**
 * Takes all arguments, combines them and creates a new error object for sentry
 * @param {...unknown} args - Console arguments to reduce
 * @returns {Error} Reduced error object
 */
function reduceConsoleArgs(args: unknown[]): Error {
  let errorMsg: unknown = args[0] || 'unbekannter Fehler';

  if (!(errorMsg instanceof Error)) {
    // stringify all args as a new Error (which creates a stack trace)
    errorMsg = new Error(
      args.reduce((accumulator: string, currentValue: unknown) => {
        return `${accumulator.toString()} ${String(currentValue)}`;
      }, '')
    );
  }

  return errorMsg as Error;
}

/**
 * Log debug messages
 */
function debug(...data: unknown[]): void {
  _console('debug', ...data);
}

/**
 * Log error messages
 */
function error(...data: unknown[]): void {
  _console('error', ...data);
}

/**
 * Log info messages
 */
function info(...data: unknown[]): void {
  _console('info', ...data);
}

/**
 * Log general messages
 */
function log(...data: unknown[]): void {
  _console('log', ...data);
}

/**
 * Start a timer
 * @param {string} data - Timer label
 * @returns {void}
 */
function time(data: string): void {
  _console('time', data);
}

/**
 * End a timer
 * @param {string} data - Timer label
 * @returns {void}
 */
function timeEnd(data: string): void {
  _console('timeEnd', data);
}

/**
 * Log trace messages
 */
function trace(...data: unknown[]): void {
  _console('trace', ...data);
}

/**
 * Log warning messages
 */
function warn(...data: unknown[]): void {
  _console('warn', ...data);
}

export { debug, error, info, log, time, timeEnd, trace, warn };
